import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { deleteKnowledgeItem } from 'shared/store/actions/knowledge.items.actions';
import { Modal } from 'src/components/design-system';
import { formatNumber } from 'shared/utils';

/**
 * Knowledge Delete Popup Component
 *
 * A reusable confirmation dialog for deleting knowledge items. Supports both single
 * item deletion and bulk deletion with automatic UI adaptation.
 *
 * Features:
 * - Single item: Shows item title in confirmation message
 * - Bulk delete: Shows count of items (never shows individual names)
 * - Custom bulk delete handler support
 * - Loading state management
 * - Internationalization support
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object|Array<{id: string}>} props.entity - Single entity object with id and title, or array of objects with IDs for bulk delete
 * @param {Function} props.onFinish - Callback fired when the popup closes (both success and dismiss)
 * @param {Function} [props.onDelete] - Callback fired after successful deletion
 * @param {Function} [props.onDismiss] - Callback fired when user cancels the operation
 * @param {Function} [props.onBulkDelete] - Optional custom bulk delete handler. If provided, this will be called instead of default bulk delete logic
 * @returns {JSX.Element|null} The delete confirmation popup or null if no entity provided
 *
 * @example
 * // Single item delete
 * <KnowledgeDeletePopup
 *   entity={{id: '123', title: 'My Document'}}
 *   onDelete={() => console.log('Item deleted')}
 *   onFinish={() => setShowPopup(false)}
 * />
 *
 * @example
 * // Bulk delete with default behavior
 * <KnowledgeDeletePopup
 *   entity={[{id: '123'}, {id: '456'}, {id: '789'}]}
 *   onDelete={() => console.log('Items deleted')}
 *   onFinish={() => setShowPopup(false)}
 * />
 *
 * @example
 * // Bulk delete with custom handler
 * <KnowledgeDeletePopup
 *   entity={[{id: '123'}, {id: '456'}]}
 *   onBulkDelete={async (items) => await myCustomBulkDelete(items)}
 *   onDelete={() => console.log('Custom bulk delete completed')}
 *   onFinish={() => setShowPopup(false)}
 * />
 */
const KnowledgeDeletePopup = React.memo(function KnowledgeDeletePopup({
  open = false,
  entity,
  onFinish,
  onDelete = () => {},
  onDismiss = () => {},
  onBulkDelete = null,
}) {
  const lang = useSelector(getLang('DELETE_KNOWLEDGE_CONFIRMATION'));
  const dispatch = useDispatch();
  const [isLoading, setLoadingState] = React.useState(false);

  // Determine if this is a bulk operation
  /** @type {boolean} Whether this is a bulk delete operation */
  const isBulkDelete = Array.isArray(entity);
  /** @type {number} Number of items to be deleted */
  const itemCount = isBulkDelete ? entity.length : 1;

  /**
   * Handles the delete confirmation action.
   * Determines whether to perform single or bulk delete and executes the appropriate logic.
   *
   * @async
   * @function
   * @returns {Promise<void>}
   */
  const deleteKnowledgeAction = React.useCallback(async () => {
    setLoadingState(true);

    // Handle bulk delete with custom handler
    if (isBulkDelete && onBulkDelete) {
      await onBulkDelete(entity);
      onFinish();
      onDelete();
      setLoadingState(false);
      return;
    }

    // Handle bulk delete with default behavior
    if (isBulkDelete) {
      const deletePromises = entity.map(item => dispatch(deleteKnowledgeItem({ id: item.id })));
      await Promise.all(deletePromises);
      onFinish();
      onDelete();
      setLoadingState(false);
      return;
    }

    // Handle single item delete
    await dispatch(deleteKnowledgeItem({ id: entity.id }));
    onFinish();
    onDelete();
    setLoadingState(false);
  }, [dispatch, entity, isBulkDelete, onBulkDelete, onFinish, onDelete]);

  /**
   * Handles the dismiss/cancel action.
   * Closes the popup without performing any deletion.
   *
   * @function
   * @returns {void}
   */
  const dismiss = React.useCallback(() => {
    // TODO: analytics

    onFinish();
    onDismiss();
  }, [onFinish, onDismiss]);

  const title = isBulkDelete ? lang.BULK_TITLE : lang.TITLE;
  const contentText = isBulkDelete
    ? lang.BULK_TEXT.replace('{count}', formatNumber(itemCount))
    : lang.TEXT.replace('{name}', entity?.title || 'this item');

  return (
    <Modal
      title={title}
      open={open}
      onCancel={dismiss}
      onSubmit={deleteKnowledgeAction}
      cancelText={lang.DISMISS}
      submitText={lang.CONFIRM}
      loading={isLoading}
      submitDisabled={isLoading}
      submitDanger
      restoreFocusOnSubmit>
      <p>{contentText}</p>
    </Modal>
  );
});

export { KnowledgeDeletePopup };
